import { Button } from '@eye-able/ui';
import { X } from 'lucide-react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  getActiveAnnouncements,
  type Announcement,
} from '@/config/announcements';
import {
  dismissAnnouncement,
  isAnnouncementDismissed,
} from '@/helpers/announcementCookies';
import { normalizeSupportedLanguage } from '@/i18n';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation';

type AnnouncementBarProps = {
  onHeightChange: (height: number) => void;
};

function findNextExpiration(announcements: Announcement[]): number | null {
  if (announcements.length === 0) return null;

  return Math.min(
    ...announcements.map((announcement) => Date.parse(announcement.expiresAt)),
  );
}

export default function AnnouncementBar({
  onHeightChange,
}: AnnouncementBarProps) {
  const { i18n, t } = useInlineTranslation();
  const [now, setNow] = useState(() => Date.now());
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(
    () => new Set(),
  );
  const barRef = useRef<HTMLDivElement>(null);

  const activeAnnouncements = useMemo(
    () =>
      getActiveAnnouncements(now).filter(
        (announcement) =>
          !dismissedIds.has(announcement.id) &&
          !isAnnouncementDismissed(announcement.id),
      ),
    [dismissedIds, now],
  );

  useEffect(() => {
    const nextExpiration = findNextExpiration(activeAnnouncements);
    if (nextExpiration === null) return;

    const timeout = window.setTimeout(
      () => setNow(Date.now()),
      Math.max(0, nextExpiration - Date.now()) + 1,
    );

    return () => window.clearTimeout(timeout);
  }, [activeAnnouncements]);

  useLayoutEffect(() => {
    const element = barRef.current;
    if (!element) {
      onHeightChange(0);
      return;
    }

    const updateHeight = () => onHeightChange(element.offsetHeight);
    updateHeight();

    if (typeof ResizeObserver === 'undefined') return;

    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(element);

    return () => resizeObserver.disconnect();
  }, [activeAnnouncements.length, onHeightChange]);

  const dismiss = (announcement: Announcement) => {
    dismissAnnouncement(announcement.id, announcement.expiresAt);
    setDismissedIds((current) => new Set(current).add(announcement.id));
  };

  if (activeAnnouncements.length === 0) return null;

  const language = normalizeSupportedLanguage(
    i18n.resolvedLanguage ?? i18n.language,
  );

  return (
    <div
      ref={barRef}
      className='eye:fixed eye:top-0 eye:left-0 eye:z-1300 eye:w-full'
      style={{ paddingRight: 'var(--removed-body-scroll-bar-size, 0px)' }}
    >
      <div className='eye:flex eye:w-full eye:flex-col'>
        {activeAnnouncements.map((announcement) => (
          <div
            key={announcement.id}
            role='status'
            aria-live='polite'
            className='eye:flex eye:w-full eye:flex-col eye:items-center eye:justify-center eye:gap-3xs eye:bg-blue-25 eye:px-s eye:py-xs eye:text-center eye:body-m eye:sm:flex-row'
          >
            <p>{announcement.messages[language]}</p>
            <Button
              size='sm'
              variant='outline'
              onClick={() => dismiss(announcement)}
            >
              <X aria-hidden />
              {t('close')}
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
